import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Trash2, Save, ArrowLeft, GripVertical, Send, CheckCircle2, Image as ImageIcon, Eye } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AdminLayout from "@/components/admin/AdminLayout";
import ImageUploader from "@/components/admin/ImageUploader";
import ArticlePreview from "@/components/admin/ArticlePreview";
import { CATEGORY_META, TYPE_LABELS } from "@/lib/constants";
import { toYoutubeEmbed, ARTICLE_STATES } from "@/lib/media";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const BLOCK_TYPES = [
  { value: "paragraph", label: "Párrafo" },
  { value: "subtitle", label: "Subtítulo" },
  { value: "quote", label: "Cita destacada" },
  { value: "image", label: "Imagen" },
  { value: "video", label: "Video (YouTube)" },
];

const emptyArticle = {
  title: "", subtitle: "", excerpt: "", content: [{ type: "paragraph", text: "" }],
  category: "actualidad", subcategory: "", author: "", author_role: "", author_avatar: "", author_bio: "",
  image: "", image_caption: "", image_alt: "", image_credit: "", gallery: [], video: "", sources: [], tags: [],
  featured: false, breaking: false, status: "draft", type: "noticia", is_demo: false,
};

function normGallery(g) {
  return (g || []).map((it) => (typeof it === "string" ? { url: it, alt: "", caption: "", credit: "" } : it));
}

export default function ArticleEditor() {
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const { can } = useAuth();
  const [form, setForm] = useState(emptyArticle);
  const [tagsInput, setTagsInput] = useState("");
  const [sourcesInput, setSourcesInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(editing);
  const [authors, setAuthors] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    api.get("/authors").then((r) => setAuthors([...(r.data.authors || []), ...(r.data.columnists || [])])).catch(() => {});
  }, []);

  useEffect(() => {
    if (editing) {
      api.get(`/admin/articles/${id}`).then((r) => {
        const a = r.data;
        setForm({ ...emptyArticle, ...a, content: a.content?.length ? a.content : [{ type: "paragraph", text: "" }], gallery: normGallery(a.gallery) });
        setTagsInput((a.tags || []).join(", "));
        setSourcesInput((a.sources || []).join(", "));
      }).catch(() => toast.error("No se pudo cargar el artículo")).finally(() => setLoading(false));
    }
  }, [id, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setBlock = (i, k, v) => setForm((f) => { const content = [...f.content]; content[i] = { ...content[i], [k]: v }; return { ...f, content }; });
  const addBlock = () => setForm((f) => ({ ...f, content: [...f.content, { type: "paragraph", text: "" }] }));
  const removeBlock = (i) => setForm((f) => ({ ...f, content: f.content.filter((_, idx) => idx !== i) }));

  const addGallery = () => setForm((f) => ({ ...f, gallery: [...f.gallery, { url: "", alt: "", caption: "", credit: "" }] }));
  const setGallery = (i, k, v) => setForm((f) => { const g = [...f.gallery]; g[i] = { ...g[i], [k]: v }; return { ...f, gallery: g }; });
  const removeGallery = (i) => setForm((f) => ({ ...f, gallery: f.gallery.filter((_, idx) => idx !== i) }));

  const pickAuthor = (val) => {
    if (val === "__custom__") { set("author_id", ""); return; }
    const a = authors.find((x) => x.id === val);
    if (a) setForm((f) => ({ ...f, author_id: a.id, author: a.name, author_role: a.role || "", author_avatar: a.avatar || "", author_bio: a.bio || "" }));
  };

  const previewForm = { ...form, tags: tagsInput.split(",").map((t) => t.trim()).filter(Boolean), gallery: form.gallery.filter((g) => g.url) };

  const save = async (statusOverride) => {
    if (!form.title.trim()) { toast.error("El título es obligatorio"); return; }
    setSaving(true);
    const payload = {
      ...form,
      status: statusOverride || form.status,
      tags: tagsInput.split(",").map((t) => t.trim()).filter(Boolean),
      sources: sourcesInput.split(",").map((t) => t.trim()).filter(Boolean),
      gallery: form.gallery.filter((g) => g.url),
    };
    try {
      if (editing) { await api.put(`/admin/articles/${id}`, payload); toast.success("Artículo actualizado"); }
      else { await api.post("/admin/articles", payload); toast.success("Artículo creado"); }
      navigate("/redaccion-nexodum");
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo guardar"); }
    finally { setSaving(false); }
  };

  if (loading) return <AdminLayout title="Editor"><div className="text-gray-400">Cargando…</div></AdminLayout>;
  const st = ARTICLE_STATES[form.status];

  return (
    <AdminLayout title={editing ? "Editar artículo" : "Nuevo artículo"}>
      <button onClick={() => navigate("/redaccion-nexodum")} className="flex items-center gap-2 text-sm text-gray-500 mb-6 hover:text-nx-dark" data-testid="editor-back"><ArrowLeft size={16} /> Volver al panel</button>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-8">
        <div className="space-y-5" data-testid="article-editor-form">
          <div><Label>Título *</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="editor-title" className="mt-1.5 text-lg" /></div>
          <div><Label>Bajada</Label><Textarea value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)} data-testid="editor-subtitle" rows={2} className="mt-1.5" /></div>
          <div><Label>Extracto (excerpt)</Label><Input value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} data-testid="editor-excerpt" className="mt-1.5" /></div>

          {/* Main image */}
          <div className="border border-gray-200 rounded-lg p-5 bg-white space-y-3">
            <ImageUploader value={form.image} onChange={(url) => set("image", url)} label="Imagen principal" testid="editor-main-image" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><Label className="text-xs">Texto alternativo (alt)</Label><Input value={form.image_alt} onChange={(e) => set("image_alt", e.target.value)} data-testid="editor-image-alt" className="mt-1" /></div>
              <div><Label className="text-xs">Epígrafe</Label><Input value={form.image_caption} onChange={(e) => set("image_caption", e.target.value)} data-testid="editor-caption" className="mt-1" /></div>
              <div><Label className="text-xs">Crédito / fuente</Label><Input value={form.image_credit} onChange={(e) => set("image_credit", e.target.value)} data-testid="editor-image-credit" className="mt-1" /></div>
            </div>
          </div>

          {/* Main video */}
          <div className="border border-gray-200 rounded-lg p-5 bg-white space-y-3">
            <Label>Video principal (URL de YouTube)</Label>
            <Input value={form.video} onChange={(e) => set("video", e.target.value)} data-testid="editor-video" placeholder="https://www.youtube.com/watch?v=…" />
            {toYoutubeEmbed(form.video) && (
              <div className="aspect-video rounded-md overflow-hidden bg-black"><iframe className="w-full h-full" src={toYoutubeEmbed(form.video)} title="preview" allowFullScreen /></div>
            )}
          </div>

          {/* Body blocks */}
          <div className="border border-gray-200 rounded-lg p-5 bg-white">
            <div className="flex items-center justify-between mb-4"><Label className="text-base">Cuerpo de la nota</Label>
              <button onClick={addBlock} type="button" data-testid="editor-add-block" className="inline-flex items-center gap-1.5 text-xs font-bold text-nx-blue"><Plus size={14} /> Agregar bloque</button>
            </div>
            <div className="space-y-4">
              {form.content.map((b, i) => (
                <div key={i} className="flex gap-2 items-start" data-testid={`editor-block-${i}`}>
                  <GripVertical size={16} className="text-gray-300 mt-3" />
                  <div className="flex-1 space-y-2">
                    <div className="flex gap-2 items-center">
                      <div className="w-40 shrink-0">
                        <Select value={b.type} onValueChange={(v) => setBlock(i, "type", v)}>
                          <SelectTrigger className="h-9 text-xs" data-testid={`block-type-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{BLOCK_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <button onClick={() => removeBlock(i)} type="button" className="p-2 text-red-400 hover:text-red-600 ml-auto"><Trash2 size={15} /></button>
                    </div>
                    {b.type === "image" ? (
                      <div className="space-y-2 bg-gray-50 rounded-md p-3">
                        <ImageUploader value={b.url} onChange={(url) => setBlock(i, "url", url)} label="" testid={`block-image-${i}`} compact />
                        <div className="grid grid-cols-3 gap-2">
                          <Input placeholder="Alt" value={b.alt || ""} onChange={(e) => setBlock(i, "alt", e.target.value)} className="text-xs" />
                          <Input placeholder="Epígrafe" value={b.caption || ""} onChange={(e) => setBlock(i, "caption", e.target.value)} className="text-xs" />
                          <Input placeholder="Crédito" value={b.credit || ""} onChange={(e) => setBlock(i, "credit", e.target.value)} className="text-xs" />
                        </div>
                      </div>
                    ) : b.type === "video" ? (
                      <div className="space-y-2">
                        <Input placeholder="URL de YouTube" value={b.url || ""} onChange={(e) => setBlock(i, "url", e.target.value)} className="text-sm" data-testid={`block-video-${i}`} />
                        {toYoutubeEmbed(b.url) && <div className="aspect-video rounded-md overflow-hidden bg-black"><iframe className="w-full h-full" src={toYoutubeEmbed(b.url)} title="preview" allowFullScreen /></div>}
                      </div>
                    ) : (
                      <Textarea value={b.text || ""} onChange={(e) => setBlock(i, "text", e.target.value)} rows={b.type === "paragraph" ? 3 : 2} placeholder="Texto del bloque…" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Gallery */}
          <div className="border border-gray-200 rounded-lg p-5 bg-white">
            <div className="flex items-center justify-between mb-4"><Label className="text-base flex items-center gap-2"><ImageIcon size={16} /> Galería de imágenes</Label>
              <button onClick={addGallery} type="button" data-testid="editor-add-gallery" className="inline-flex items-center gap-1.5 text-xs font-bold text-nx-blue"><Plus size={14} /> Agregar imagen</button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {form.gallery.map((g, i) => (
                <div key={i} className="bg-gray-50 rounded-md p-3 space-y-2" data-testid={`gallery-item-${i}`}>
                  <ImageUploader value={g.url} onChange={(url) => setGallery(i, "url", url)} label="" testid={`gallery-uploader-${i}`} compact />
                  <Input placeholder="Alt" value={g.alt} onChange={(e) => setGallery(i, "alt", e.target.value)} className="text-xs" />
                  <Input placeholder="Epígrafe" value={g.caption} onChange={(e) => setGallery(i, "caption", e.target.value)} className="text-xs" />
                  <div className="flex gap-2"><Input placeholder="Crédito" value={g.credit} onChange={(e) => setGallery(i, "credit", e.target.value)} className="text-xs" />
                    <button type="button" onClick={() => removeGallery(i)} className="p-2 text-red-400"><Trash2 size={14} /></button></div>
                </div>
              ))}
            </div>
          </div>

          <div><Label>Fuentes / enlaces (separados por coma)</Label><Input value={sourcesInput} onChange={(e) => setSourcesInput(e.target.value)} data-testid="editor-sources" className="mt-1.5" /></div>
        </div>

        {/* sidebar */}
        <aside className="space-y-5">
          <div className="border border-gray-200 rounded-lg p-5 bg-white space-y-3">
            {editing && <div className="text-xs mb-1">Estado actual: <span className={`font-bold px-2 py-0.5 rounded ${st?.bg}`}>{st?.label}</span></div>}
            {can("publish") && (
              <button onClick={() => save("published")} disabled={saving} data-testid="editor-publish"
                className="w-full nx-gradient-bg text-white font-bold py-2.5 rounded-md text-xs uppercase tracking-wider disabled:opacity-50">Publicar</button>
            )}
            {can("submit") && !can("publish") && (
              <button onClick={() => save("in_review")} disabled={saving} data-testid="editor-submit-review"
                className="w-full bg-nx-violet text-white font-bold py-2.5 rounded-md text-xs uppercase tracking-wider inline-flex items-center justify-center gap-1.5 disabled:opacity-50"><Send size={14} /> Enviar a revisión</button>
            )}
            {can("submit") && can("publish") && (
              <button onClick={() => save("in_review")} disabled={saving} data-testid="editor-submit-review"
                className="w-full border border-nx-violet text-nx-violet font-bold py-2.5 rounded-md text-xs uppercase tracking-wider inline-flex items-center justify-center gap-1.5 disabled:opacity-50"><Send size={14} /> Enviar a revisión</button>
            )}
            <button onClick={() => save("draft")} disabled={saving} data-testid="editor-save-draft"
              className="w-full border border-gray-300 text-nx-dark font-bold py-2.5 rounded-md text-xs uppercase tracking-wider inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
              <Save size={14} /> Guardar borrador
            </button>
            <button onClick={() => setShowPreview(true)} type="button" data-testid="editor-preview"
              className="w-full text-nx-blue font-bold py-2 rounded-md text-xs uppercase tracking-wider inline-flex items-center justify-center gap-1.5 hover:bg-nx-blue/5">
              <Eye size={14} /> Vista previa
            </button>
            <p className="text-[11px] text-gray-400">{can("publish") ? "Podés publicar directamente o enviar a revisión." : "Tu rol guarda y envía a revisión; la publicación queda a cargo de un editor."}</p>
          </div>

          <div className="border border-gray-200 rounded-lg p-5 bg-white space-y-4">
            <div><Label>Sección</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="mt-1.5" data-testid="editor-category"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(CATEGORY_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Subcategoría</Label><Input value={form.subcategory} onChange={(e) => set("subcategory", e.target.value)} data-testid="editor-subcategory" className="mt-1.5" /></div>
            <div><Label>Tipo</Label>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger className="mt-1.5" data-testid="editor-type"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(TYPE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Etiquetas (separadas por coma)</Label><Input value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} data-testid="editor-tags" className="mt-1.5" /></div>
            <div className="flex items-center gap-2"><Checkbox id="feat" checked={form.featured} onCheckedChange={(v) => set("featured", !!v)} data-testid="editor-featured" /><Label htmlFor="feat" className="font-normal">Noticia destacada / principal</Label></div>
            <div className="flex items-center gap-2"><Checkbox id="brk" checked={form.breaking} onCheckedChange={(v) => set("breaking", !!v)} data-testid="editor-breaking" /><Label htmlFor="brk" className="font-normal">Última hora</Label></div>
          </div>

          <div className="border border-gray-200 rounded-lg p-5 bg-white space-y-4">
            <div><Label>Seleccionar autor</Label>
              <Select value={form.author_id || "__custom__"} onValueChange={pickAuthor}>
                <SelectTrigger className="mt-1.5" data-testid="editor-author-select"><SelectValue placeholder="Elegí un autor" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__custom__">Otro (personalizado)</SelectItem>
                  {authors.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}{a.role ? ` · ${a.role}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Autor</Label><Input value={form.author} onChange={(e) => set("author", e.target.value)} data-testid="editor-author" className="mt-1.5" /></div>
            <div><Label>Rol del autor</Label><Input value={form.author_role} onChange={(e) => set("author_role", e.target.value)} className="mt-1.5" /></div>
            <ImageUploader value={form.author_avatar} onChange={(url) => set("author_avatar", url)} label="Foto del autor" testid="editor-author-avatar" compact />
            <div><Label>Bio del columnista</Label><Textarea value={form.author_bio} onChange={(e) => set("author_bio", e.target.value)} rows={3} className="mt-1.5" /></div>
          </div>
        </aside>
      </div>
      <ArticlePreview open={showPreview} onOpenChange={setShowPreview} form={previewForm} />
    </AdminLayout>
  );
}
